import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Lock, User, Key, Shield, Eye, EyeOff, CheckCircle, 
  AlertTriangle, Mail, Users, UserPlus, LogOut, 
  RefreshCw, Settings, Activity, Link2, UserCheck 
} from 'lucide-react';

// ============= Crypto Utilities =============
const cryptoUtils = {
  generateSalt: () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  },

  deriveKey: async (password, salt, iterations = 210000) => {
    const encoder = new TextEncoder();
    
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );
    
    const saltBytes = new Uint8Array(
      salt.match(/.{2}/g).map(byte => parseInt(byte, 16))
    );
    
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: saltBytes,
        iterations,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
  },

  generateEncryptionKey: async () => {
    return crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
  },

  encrypt: async (data, key) => {
    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoder.encode(typeof data === 'string' ? data : JSON.stringify(data))
    );
    
    return {
      iv: Array.from(iv, byte => byte.toString(16).padStart(2, '0')).join(''),
      data: Array.from(new Uint8Array(encrypted), byte => byte.toString(16).padStart(2, '0')).join('')
    };
  },

  decrypt: async (encryptedData, key) => {
    const iv = new Uint8Array(encryptedData.iv.match(/.{2}/g).map(byte => parseInt(byte, 16)));
    const data = new Uint8Array(encryptedData.data.match(/.{2}/g).map(byte => parseInt(byte, 16)));
    
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      data
    );
    
    const decoder = new TextDecoder();
    const decryptedText = decoder.decode(decrypted);
    
    try {
      return JSON.parse(decryptedText);
    } catch {
      return decryptedText;
    }
  }
};

const API_URL = 'http://localhost:8000';

// ============= Separate Form Components =============
function RegistrationForm({ onRegister, loading }) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onRegister({ email, username, password, firstName, lastName });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-xl font-bold text-white mb-4">Register New Account</h2>
      
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email (not encrypted)"
          className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-400 transition-colors"
          required
        />
      </div>

      <div className="relative">
        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Username"
          className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-400 transition-colors"
          required
        />
      </div>

      <div className="relative">
        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-400 transition-colors"
          required
        />
      </div>

      <div className="relative">
        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First Name (will be encrypted)"
          className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-400 transition-colors"
        />
      </div>

      <div className="relative">
        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Last Name (will be encrypted)"
          className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-400 transition-colors"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50"
      >
        {loading ? 'Registering...' : 'Register'}
      </button>
    </form>
  );
}

function LoginForm({ onLogin, loading }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin({ email, password });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-xl font-bold text-white mb-4">Login</h2>
      
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-400 transition-colors"
          required
        />
      </div>

      <div className="relative">
        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-400 transition-colors"
          required
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50"
      >
        {loading ? 'Logging in...' : 'Login'}
      </button>
    </form>
  );
}

function EmailVerificationForm({ onVerify, loading }) {
  const [token, setToken] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onVerify(token);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-xl font-bold text-white mb-4">Verify Email</h2>
      
      <div className="relative">
        <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Verification Token"
          className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-400 transition-colors"
          required
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50"
      >
        {loading ? 'Verifying...' : 'Verify Email'}
      </button>
    </form>
  );
}

// ============= Main Application Component =============
export default function MedicalE2ETestApp() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessionToken, setSessionToken] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [encryptionKey, setEncryptionKey] = useState(null);
  const [activeTab, setActiveTab] = useState('register');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [userData, setUserData] = useState(null);
  const [subordinates, setSubordinates] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [systemStats, setSystemStats] = useState(null);
  const [encryptedNotes, setEncryptedNotes] = useState('');
  const [decryptedNotes, setDecryptedNotes] = useState('');
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [bindingType, setBindingType] = useState('supervision');
  const [invitationCode, setInvitationCode] = useState('');

  const showMessage = useCallback((type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  }, []);

  const handleRegister = useCallback(async (data) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          username: data.username,
          password: data.password,
          first_name: data.firstName,
          last_name: data.lastName
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        showMessage('success', 'Registration successful! Check console for verification link.');
        console.log('Verification Link:', result.data.verification_link);
        setActiveTab('verify');
      } else {
        showMessage('error', result.error || 'Registration failed');
      }
    } catch (err) {
      showMessage('error', 'Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  const handleLogin = useCallback(async (data) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          password: data.password
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setIsLoggedIn(true);
        setSessionToken(result.data.session_token);
        setCurrentUser(result.data.user);
        
        const salt = cryptoUtils.generateSalt();
        const key = await cryptoUtils.deriveKey(data.password, salt);
        setEncryptionKey(key);
        
        showMessage('success', 'Login successful!');
        setActiveTab('profile');
      } else {
        showMessage('error', result.error || 'Login failed');
      }
    } catch (err) {
      showMessage('error', 'Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  const handleVerifyEmail = useCallback(async (token) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      
      const result = await response.json();
      
      if (result.success) {
        showMessage('success', 'Email verified! You can now login.');
        setActiveTab('login');
      } else {
        showMessage('error', result.error || 'Verification failed');
      }
    } catch (err) {
      showMessage('error', 'Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      setIsLoggedIn(false);
      setSessionToken('');
      setCurrentUser(null);
      setEncryptionKey(null);
      showMessage('success', 'Logged out successfully');
      setActiveTab('login');
    } catch (err) {
      showMessage('error', 'Logout error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, showMessage]);

  const fetchUserProfile = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/users/me`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUserData(data.data);
        showMessage('success', 'Profile loaded');
      } else {
        showMessage('error', data.error || 'Failed to fetch profile');
      }
    } catch (err) {
      showMessage('error', 'Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, showMessage]);

  const createInvitation = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bindings/invite`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          invitee_email: inviteeEmail,
          binding_type: bindingType,
          message: 'Welcome to our team!'
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        showMessage('success', `Invitation sent!`);
        console.log('Invitation Link:', data.data.invitation_link);
        setInviteeEmail('');
      } else {
        showMessage('error', data.error || 'Failed to create invitation');
      }
    } catch (err) {
      showMessage('error', 'Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [inviteeEmail, bindingType, sessionToken, showMessage]);

  const acceptInvitation = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bindings/accept`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({ invitation_code: invitationCode })
      });
      
      const data = await response.json();
      
      if (data.success) {
        showMessage('success', 'Invitation accepted!');
        setInvitationCode('');
      } else {
        showMessage('error', data.error || 'Failed to accept invitation');
      }
    } catch (err) {
      showMessage('error', 'Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [invitationCode, sessionToken, showMessage]);

  const TabButton = ({ name, icon: Icon, label }) => (
    <button
      onClick={() => setActiveTab(name)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
        activeTab === name 
          ? 'bg-purple-600 text-white' 
          : 'bg-white/10 text-gray-300 hover:bg-white/20'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-10 h-10 text-purple-400" />
              <div>
                <h1 className="text-2xl font-bold text-white">Medical E2E Encryption Test Suite</h1>
                <p className="text-gray-300">Test all API functionalities with encryption</p>
              </div>
            </div>
            {isLoggedIn && (
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-white font-semibold">{currentUser?.email}</p>
                  <p className="text-xs text-gray-400">Session Active</p>
                </div>
                <button
                  onClick={logout}
                  className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {!isLoggedIn ? (
            <>
              <TabButton name="register" icon={UserPlus} label="Register" />
              <TabButton name="login" icon={User} label="Login" />
              <TabButton name="verify" icon={Mail} label="Verify Email" />
            </>
          ) : (
            <>
              <TabButton name="profile" icon={User} label="Profile" />
              <TabButton name="encryption" icon={Lock} label="Encryption Test" />
              <TabButton name="bindings" icon={Users} label="User Bindings" />
            </>
          )}
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' 
              ? 'bg-green-500/20 border border-green-500/30 text-green-400'
              : 'bg-red-500/20 border border-red-500/30 text-red-400'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        {/* Content Area */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
          {activeTab === 'register' && !isLoggedIn && (
            <RegistrationForm onRegister={handleRegister} loading={loading} />
          )}

          {activeTab === 'verify' && !isLoggedIn && (
            <EmailVerificationForm onVerify={handleVerifyEmail} loading={loading} />
          )}

          {activeTab === 'login' && !isLoggedIn && (
            <LoginForm onLogin={handleLogin} loading={loading} />
          )}

          {activeTab === 'profile' && isLoggedIn && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">User Profile</h2>
                <button
                  onClick={fetchUserProfile}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Fetch Profile
                </button>
              </div>
              
              {userData && (
                <div className="bg-black/20 rounded-lg p-4 space-y-2">
                  <p className="text-gray-300">
                    <span className="text-gray-500">Email:</span> {userData.user.email}
                  </p>
                  <p className="text-gray-300">
                    <span className="text-gray-500">Username:</span> {userData.user.username}
                  </p>
                  <p className="text-gray-300">
                    <span className="text-gray-500">Roles:</span> {userData.roles?.length || 0}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'encryption' && isLoggedIn && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white mb-4">Encryption Test</h2>
              
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="text-green-400 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Encryption key generated from your password
                </p>
              </div>
              
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-300">
                  Enter sensitive data to encrypt:
                </label>
                <textarea
                  value={encryptedNotes}
                  onChange={(e) => setEncryptedNotes(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-400"
                  rows="4"
                  placeholder="Enter any sensitive information..."
                />
                <button
                  onClick={async () => {
                    if (encryptionKey && encryptedNotes) {
                      const encrypted = await cryptoUtils.encrypt(encryptedNotes, encryptionKey);
                      console.log('Encrypted:', encrypted);
                      const decrypted = await cryptoUtils.decrypt(encrypted, encryptionKey);
                      setDecryptedNotes(decrypted);
                      showMessage('success', 'Data encrypted!');
                    }
                  }}
                  disabled={loading || !encryptedNotes}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50"
                >
                  Encrypt & Decrypt
                </button>
                
                {decryptedNotes && (
                  <div className="bg-black/20 rounded-lg p-4">
                    <p className="text-sm text-gray-400 mb-2">Decrypted Result:</p>
                    <p className="text-white font-mono">{decryptedNotes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'bindings' && isLoggedIn && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white mb-4">User Bindings</h2>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Create Invitation</h3>
                <input
                  type="email"
                  value={inviteeEmail}
                  onChange={(e) => setInviteeEmail(e.target.value)}
                  placeholder="Invitee Email"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-400"
                />
                <select
                  value={bindingType}
                  onChange={(e) => setBindingType(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-400"
                >
                  <option value="supervision">Supervision</option>
                  <option value="mentorship">Mentorship</option>
                  <option value="delegation">Delegation</option>
                </select>
                <button
                  onClick={createInvitation}
                  disabled={loading || !inviteeEmail}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50"
                >
                  Send Invitation
                </button>
              </div>
              
              <div className="space-y-4 pt-4 border-t border-white/10">
                <h3 className="text-lg font-semibold text-white">Accept Invitation</h3>
                <input
                  type="text"
                  value={invitationCode}
                  onChange={(e) => setInvitationCode(e.target.value)}
                  placeholder="Invitation Code"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-400"
                />
                <button
                  onClick={acceptInvitation}
                  disabled={loading || !invitationCode}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50"
                >
                  Accept Invitation
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 p-4 bg-white/5 rounded-lg">
          <p className="text-xs text-gray-400 flex items-center gap-2">
            <Lock className="w-3 h-3" />
            All data except email addresses is encrypted using AES-256-GCM with PBKDF2 key derivation (210,000 iterations)
          </p>
        </div>
      </div>
    </div>
  );
}